import { useState } from 'react';

import { createDataTableColumnResizePreview } from '@/UI/DataTable/columnResizePreview';
import { clampDataTableColumnWidth } from '@/UI/DataTable/columnWidth';

import type {
  CSSProperties,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  TdHTMLAttributes,
} from 'react';

type HeaderCellDomProps = HTMLAttributes<HTMLTableCellElement> &
  TdHTMLAttributes<HTMLTableCellElement>;

type DataTableHeaderCellProps = HeaderCellDomProps & {
  columnId?: string;
  columnWidth?: number;
  columnMinWidth?: number;
  onColumnResize?: (columnId: string, width: number) => void;
};

/**
 * antd types `onHeaderCell` as returning plain DOM attributes, so the resize
 * metadata is widened here at the single hand-off point instead of at each column.
 */
export function toDataTableHeaderCellProps(
  props: DataTableHeaderCellProps,
): HeaderCellDomProps {
  return props;
}

const HANDLE_HIT_AREA = 8;
const HANDLE_LINE_WIDTH = 2;

const HANDLE_STYLE: CSSProperties = {
  position: 'absolute',
  insetBlock: 0,
  insetInlineEnd: 0,
  width: HANDLE_HIT_AREA,
  cursor: 'col-resize',
  touchAction: 'none',
  userSelect: 'none',
  zIndex: 1,
};

const LINE_STYLE: CSSProperties = {
  position: 'absolute',
  insetBlock: '25%',
  insetInlineEnd: 0,
  width: HANDLE_LINE_WIDTH,
  borderRadius: HANDLE_LINE_WIDTH,
  backgroundColor: 'var(--ant-table-border-color)',
  transition: 'background-color var(--ant-motion-duration-mid, 0.2s)',
};

const ACTIVE_LINE_STYLE: CSSProperties = {
  ...LINE_STYLE,
  backgroundColor: 'var(--ant-color-primary)',
};

function stopClick(event: ReactMouseEvent<HTMLSpanElement>) {
  // The grip sits inside the header button area; without this a tap toggles sorting.
  event.stopPropagation();
}

/**
 * Header cell for `DataTable` that adds a drag-to-resize grip on its trailing edge.
 * Cells antd owns (selection, expand) receive no resize props and render untouched.
 */
export function DataTableResizableHeaderCell({
  columnId,
  columnWidth,
  columnMinWidth,
  onColumnResize,
  children,
  ...cellProps
}: DataTableHeaderCellProps) {
  const [hovered, setHovered] = useState(false);
  const [resizing, setResizing] = useState(false);

  const resizable = columnId !== undefined && onColumnResize !== undefined;

  const startResize = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (event.button !== 0 || !resizable) return;

    event.preventDefault();
    event.stopPropagation();

    const grip = event.currentTarget;
    const headerCell = grip.closest('th');
    const startX = event.clientX;
    const startWidth =
      columnWidth ?? headerCell?.getBoundingClientRect().width ?? 0;
    const preview = headerCell
      ? createDataTableColumnResizePreview(headerCell, startWidth)
      : null;
    const previousCursor = document.body.style.cursor;
    let frame = 0;
    let width = startWidth;

    const paint = () => {
      frame = 0;
      preview?.update(width);
    };

    const trackPointer = (moveEvent: PointerEvent) => {
      width = clampDataTableColumnWidth(
        startWidth + (moveEvent.clientX - startX),
        columnMinWidth,
      );
      // pointermove outruns paints; coalesce to one DOM write per frame.
      if (frame) return;
      frame = requestAnimationFrame(paint);
    };

    const stopResize = () => {
      grip.removeEventListener('pointermove', trackPointer);
      grip.removeEventListener('pointerup', stopResize);
      grip.removeEventListener('pointercancel', stopResize);
      if (frame) cancelAnimationFrame(frame);
      document.body.style.cursor = previousCursor;
      setResizing(false);
      // The only React commit of the drag: everything above was paint-only.
      if (width !== startWidth) onColumnResize(columnId, width);
    };

    grip.setPointerCapture(event.pointerId);
    grip.addEventListener('pointermove', trackPointer);
    grip.addEventListener('pointerup', stopResize);
    grip.addEventListener('pointercancel', stopResize);
    // Keep the resize cursor while the pointer roams outside the grip.
    document.body.style.cursor = 'col-resize';
    setResizing(true);
  };

  return (
    <th {...cellProps}>
      {children}
      {resizable ? (
        <span
          aria-hidden="true"
          style={HANDLE_STYLE}
          onPointerDown={startResize}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          onClick={stopClick}
        >
          <span style={hovered || resizing ? ACTIVE_LINE_STYLE : LINE_STYLE} />
        </span>
      ) : null}
    </th>
  );
}
